/**
 * TravelCriticPanel — right-drawer that runs the sectioned trip critique
 * and renders the six findings panels. The Trip Analysis button in the
 * planner header opens this panel; the user clicks "Run analysis" to
 * trigger the model call (it's an expensive-ish call, so we don't
 * auto-run on open).
 */

import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Gauge, Loader2, RefreshCw } from 'lucide-react';
import { travelApi } from '../api';
import type { TravelCriticSignalsResponse } from '../api';
import { useTravelFeature } from '../hooks';

/** Display order for the six sections — matches the server's emit_critique
 * tool keys, but using user-facing labels. */
const SECTION_ORDER = [
  'Pacing',
  'Logistics',
  'Operating hours',
  'Reservations',
  'Health & jetlag',
  'Missing basics',
] as const;

interface Props {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CritiqueState {
  sections: Record<string, string>;
  signals: TravelCriticSignalsResponse;
}

export function TravelCriticPanel({ tripId, open, onOpenChange }: Props) {
  const feature = useTravelFeature('critic');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CritiqueState | null>(null);

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await travelApi.runTravelCritic(tripId);
      setResult({ sections: res.sections, signals: res.signals });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Gauge className="h-5 w-5" /> Trip Analysis
          </SheetTitle>
          <SheetDescription>
            A sectioned critique of pacing, logistics, operating hours, reservations, health risks,
            and missing basics — informed by your current itinerary.
          </SheetDescription>
        </SheetHeader>

        {!feature.enabled ? (
          <div className="mt-4 text-sm text-muted-foreground">
            Trip analysis is disabled. Enable it from Settings → Trip Analysis.
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <Button onClick={run} disabled={running} variant={result ? 'outline' : 'default'}>
              {running ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing…
                </>
              ) : result ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" /> Re-run analysis
                </>
              ) : (
                <>
                  <Gauge className="h-4 w-4 mr-2" /> Run analysis
                </>
              )}
            </Button>

            {error && <div className="text-sm text-destructive">{error}</div>}

            {running && !result && (
              <div className="space-y-2">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            )}

            {result && (
              <div className="space-y-3">
                <div className="text-xs text-muted-foreground bg-muted/30 border border-border rounded-md p-2.5 leading-relaxed">
                  <strong className="font-medium">Heads up:</strong> this analysis is an
                  AI-generated starting point. Treat the call-outs as things worth considering —
                  verify reservation requirements, opening hours, and any health/safety items
                  against the official source before relying on them.
                </div>
                {SECTION_ORDER.map((section) => {
                  const body = result.sections[section] ?? '';
                  const isSolid = body.trim() === 'Looks solid.';
                  return (
                    <section
                      key={section}
                      className="border border-border rounded-md p-3 space-y-1.5 bg-muted/20"
                    >
                      <h3 className="text-sm font-semibold">{section}</h3>
                      <div
                        className={`text-xs whitespace-pre-wrap ${
                          isSolid ? 'text-muted-foreground italic' : ''
                        }`}
                      >
                        {body}
                      </div>
                    </section>
                  );
                })}
                <details className="border border-border rounded-md p-3 bg-muted/10">
                  <summary className="text-xs font-medium cursor-pointer text-muted-foreground">
                    Signals (raw)
                  </summary>
                  <pre className="mt-2 text-[10px] leading-tight overflow-x-auto">
                    {JSON.stringify(result.signals, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
